import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";

import { CaseAdmin, CaseSpecimenAdmin, SpecimenProtocolOverride } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { SlideAdminWithUrls } from "shared/reducers/admin/adminSlideReducer";
import {
  getProtocolStatus,
  procedureEnumToLabel,
  protocolLabels,
  specimen_source,
} from "shared/utils/specimenUtils";

import HiddenDropdown from "../filters/HiddenDropdown";
import SlidesTable from "../SlidesTable/SlidesTable";

type SpecimensProps = {
  adminCase: CaseAdminDetail;
  specimens: SpecimensObject;
  canUpdateCase: boolean;
};

export type SpecimensObject = {
  [key: string]: {
    specimen: CaseSpecimenAdmin;
    slides: SlideAdminWithUrls[];
  };
};

const ErrorMessage = ({ message }: { message: string }) => {
  return (
    <span style={{ color: "var(--imperialRed)" }}>
      <FontAwesomeIcon icon="exclamation-circle" color="var(--imperialRed)" />{" "}
      {message}
    </span>
  );
};

const Specimens = ({ adminCase, specimens, canUpdateCase }: SpecimensProps) => {
  return (
    <div className="panelContainer panelContainer--gap">
      <ReactTooltip />
      {Object.keys(specimens)
        .sort((a, b) =>
          a.localeCompare(b, "en", {
            numeric: true,
            sensitivity: "base",
          })
        )
        .map((key: string, index: number) => {
          const slides = specimens[key].slides;
          const specimenSource = specimen_source(specimens[key].specimen);
          return (
            <div
              className="SpecimensSection panelContainer__layout--vertical"
              key={`${key}:${index}`}
            >
              <HiddenDropdown
                type="largeLabel"
                label={
                  <>
                    <span>Specimen {specimens[key].specimen.name}</span>
                    {canUpdateCase && (
                      <Link
                        to={`/admin/cases/${adminCase.id}/specimens/${specimens[key].specimen.id}`}
                        onClick={(e: any) => {
                          e.stopPropagation();
                        }}
                      >
                        <FontAwesomeIcon
                          icon="pencil-alt"
                          color="var(--steel)"
                        />
                      </Link>
                    )}

                    {adminCase.source === CaseAdmin.source.PW &&
                      specimens[key].specimen.protocols?.map((p) => (
                        <div
                          key={p}
                          style={{ marginBottom: "4px" }}
                          className="Tag Tag--blue"
                          data-tip={
                            getProtocolStatus(
                              specimens[key].specimen,
                              p as SpecimenProtocolOverride.protocol
                            ).reason
                          }
                        >
                          {protocolLabels[p]}
                        </div>
                      ))}
                  </>
                }
                hasError={!slides.length}
                isInitOpen
              >
                <div className="informationContainer">
                  <label>Collected Date</label>
                  <span>
                    {specimens[key].specimen.collected_date ? (
                      `${DateTime.fromISO(
                        specimens[key].specimen.collected_date || "",
                        { zone: adminCase.collected_date_timezone || "UTC" }
                      ).toLocaleString(DateTime.DATETIME_MED)} ${
                        adminCase.collected_date_timezone
                      }`
                    ) : (
                      <ErrorMessage message="Missing Collected Date" />
                    )}
                  </span>
                  <label>Received Date</label>
                  <span>
                    {specimens[key].specimen.received_date ? (
                      `${DateTime.fromISO(
                        specimens[key].specimen.received_date || "",
                        { zone: adminCase.received_date_timezone || "UTC" }
                      ).toLocaleString(DateTime.DATETIME_MED)} ${
                        adminCase.received_date_timezone
                      }`
                    ) : (
                      <ErrorMessage message="Missing Received Date" />
                    )}
                  </span>
                  <label>Source:</label>
                  <span>
                    {specimenSource ? (
                      specimenSource
                    ) : adminCase.source === CaseAdmin.source.WPL ? (
                      <ErrorMessage message="Missing Source Information" />
                    ) : (
                      <span>
                        {specimens[key].specimen.type || (
                          <ErrorMessage message="Missing Type" />
                        )}
                        {", "}
                        {specimens[key].specimen.site?.toUpperCase() || (
                          <ErrorMessage message="Missing Site" />
                        )}
                        {", "}
                        {specimens[key].specimen.procedure ? (
                          procedureEnumToLabel(
                            specimens[key].specimen.procedure
                          )
                        ) : (
                          <ErrorMessage message="Missing Procedure" />
                        )}
                      </span>
                    )}
                  </span>
                  <label>Clinical History:</label>
                  <span>
                    {specimens[key].specimen.clinical_history ? (
                      specimens[key].specimen.clinical_history
                    ) : (
                      <ErrorMessage message="Missing Clinical History" />
                    )}
                  </span>
                  <label>Gross Description:</label>
                  <span>
                    {specimens[key].specimen.gross_description ? (
                      specimens[key].specimen.gross_description
                    ) : (
                      <ErrorMessage message="Missing Gross Description" />
                    )}
                  </span>
                </div>
              </HiddenDropdown>
              {slides.length ? (
                <SlidesTable
                  slides={slides}
                  slideEditUrl={`/admin/cases/${adminCase.id}/slides/`}
                />
              ) : (
                <span>There are no slides in this specimen</span>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default Specimens;
